import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ClientPlatformSummary, CacheInfo } from '@shared/types';
import { API_BASE_URL } from '@/client/config';
import { handleApiError } from '@/client/lib/apiError';

interface ClientPlatformState {
  // Map of clientID -> platform summary
  summaryByClient: Record<string, ClientPlatformSummary>;
  cacheInfoByClient: Record<string, CacheInfo | null>;
  loading: boolean;
  error: string | null;
  fetchSummary: (
    clientID: string,
    logout: () => void,
    accessToken: string,
    forceRefresh?: boolean
  ) => Promise<void>;
  getSummary: (clientID: string) => ClientPlatformSummary | null;
}

export const useClientPlatformStore = create<ClientPlatformState>()(devtools((set, get) => ({
  summaryByClient: {},
  cacheInfoByClient: {},
  loading: false,
  error: null,
  getSummary: (clientID) => get().summaryByClient[clientID] || null,
  fetchSummary: async (clientID, logout, accessToken, forceRefresh = false) => {
    if (!forceRefresh && get().summaryByClient[clientID]) {
      return;
    }

    set({ loading: true, error: null });
    try {
      const response = await fetch(`${API_BASE_URL}/api/clients/${clientID}/platform-summary`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (handleApiError(response, logout)) return;
        throw new Error(`Failed to fetch platform summary: ${response.statusText}`);
      }

      const data = await response.json();
      set((state) => ({
        summaryByClient: { ...state.summaryByClient, [clientID]: data.summary },
        cacheInfoByClient: { ...state.cacheInfoByClient, [clientID]: data.cacheInfo ?? null },
        loading: false,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      set({ error: errorMessage, loading: false });
    }
  },
}), { name: 'client-platform-store' }));
